import React from 'react'
import { Form, Row, Col, DropdownButton, Dropdown } from 'react-bootstrap'

// definitions
import { AccountXeroOrgWithData } from '../../../../screens/subsync/subsync'
import { XeroAPIContact } from '../../../../../../back-end/utilities/apiDefinitions'
import { DetailedStripeOrgAccountXeroOrg } from '../settings'

interface CustomerBalanceSettingsProps {
	orgMapping: DetailedStripeOrgAccountXeroOrg | undefined
	mappedXeroOrg: AccountXeroOrgWithData | undefined
	stripeOrgID: string
	handleSettingsUpdate: (stripeOrgID: string, values: { [name: string]: string | boolean | XeroAPIContact | undefined }) => void
}

const CustomerBalanceSettings = (props: CustomerBalanceSettingsProps) => {
	const [searchFilter, setSearchFilter] = React.useState<string>('')
	const [taxFilter, setTaxFilter] = React.useState<string>('')

	return (
		<>
			<Form.Group as={Row} className="d-flex align-items-center" style={{ height: '2.5rem' }}>
				<Col sm="2">
					<Form.Label htmlFor={`${props.stripeOrgID}-customer-balance-enabled`} className="mb-0">
						Enable invoices with customer balance transactions
					</Form.Label>
				</Col>
				<Col>
					<Form.Check
						type={'switch'}
						id={`${props.stripeOrgID}-customer-balance-enabled`}
						name={'customerBalanceEnabled'}
						checked={!!props.orgMapping?.stripeOrgAccountXeroOrgSettings?.customerBalanceEnabled}
						onChange={(e) => props.handleSettingsUpdate(props.stripeOrgID, { [e.target.name]: e.target.checked })}
					/>
				</Col>
			</Form.Group>
			<Form.Group as={Row} style={{ height: '2.5rem' }}>
				<Form.Label column sm={2}>
					Xero Account
				</Form.Label>
				<Col sm={4}>
					<DropdownButton
						id={`${props.stripeOrgID}-customer-balance-account-select`}
						title={
							props.mappedXeroOrg!.accounts.find(
								(a) => a.xeroAccountID === props.orgMapping?.stripeOrgAccountXeroOrgSettings?.customerBalanceXeroAccountID
							)?.name || 'Select Xero Account'
						}
						onSelect={(eventKey) => {
							props.handleSettingsUpdate(props.stripeOrgID, { customerBalanceXeroAccountID: eventKey! })
							setSearchFilter('')
						}}
						disabled={!props.orgMapping?.stripeOrgAccountXeroOrgSettings?.customerBalanceEnabled}
					>
						<Dropdown.Item as="div">
							<Form.Control
								type="text"
								placeholder="Search or enter value"
								value={searchFilter}
								onClick={(e: React.MouseEvent<HTMLInputElement>) => e.stopPropagation()} // Prevent dropdown from closing on input click
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchFilter(e.target.value)} // Handle input changes
							/>
						</Dropdown.Item>
						<Dropdown.Header>Revenue</Dropdown.Header>
						{props
							.mappedXeroOrg!.accounts.filter(
								(account) => account.type === 'REVENUE' && account.name!.toLowerCase().includes(searchFilter.toLowerCase())
							)
							.sort((a, b) => a.name!.localeCompare(b.name!, undefined, { numeric: true }))
							.map((xa) => (
								<Dropdown.Item key={xa.xeroAccountID} eventKey={xa.xeroAccountID}>
									{xa.name}
								</Dropdown.Item>
							))}
						<Dropdown.Header>Other Income</Dropdown.Header>
						{props
							.mappedXeroOrg!.accounts.filter(
								(account) => account.type === 'OTHERINCOME' && account.name!.toLowerCase().includes(searchFilter.toLowerCase())
							)
							.sort((a, b) => a.name!.localeCompare(b.name!, undefined, { numeric: true }))
							.map((xa) => (
								<Dropdown.Item key={xa.xeroAccountID} eventKey={xa.xeroAccountID}>
									{xa.name}
								</Dropdown.Item>
							))}
						<Dropdown.Header>Sales</Dropdown.Header>
						{props
							.mappedXeroOrg!.accounts.filter(
								(account) => account.type === 'SALES' && account.name!.toLowerCase().includes(searchFilter.toLowerCase())
							)
							.sort((a, b) => a.name!.localeCompare(b.name!, undefined, { numeric: true }))
							.map((xa) => (
								<Dropdown.Item key={xa.xeroAccountID} eventKey={xa.xeroAccountID}>
									{xa.name}
								</Dropdown.Item>
							))}
						<Dropdown.Header>Current Liability</Dropdown.Header>
						{props
							.mappedXeroOrg!.accounts.filter(
								(account) => account.type === 'CURRLIAB' && account.name!.toLowerCase().includes(searchFilter.toLowerCase())
							)
							.sort((a, b) => a.name!.localeCompare(b.name!, undefined, { numeric: true }))
							.map((xa) => (
								<Dropdown.Item key={xa.xeroAccountID} eventKey={xa.xeroAccountID}>
									{xa.name}
								</Dropdown.Item>
							))}
					</DropdownButton>
				</Col>
			</Form.Group>
			<Form.Group as={Row} style={{ height: '2.5rem' }}>
				<Form.Label column sm={2}>
					Xero Tax Rate
				</Form.Label>
				<Col sm={4}>
					<DropdownButton
						id={`${props.stripeOrgID}-customer-balance-tax-rate-select`}
						title={
							props.mappedXeroOrg!.taxRates.find(
								(tr) => tr.xeroTaxRateID === props.orgMapping?.stripeOrgAccountXeroOrgSettings?.customerBalanceXeroTaxRateID
							)?.name || 'Select Xero Tax Rate'
						}
						onSelect={(eventKey) => {
							props.handleSettingsUpdate(props.stripeOrgID, { customerBalanceXeroTaxRateID: eventKey! })
							setTaxFilter('')
						}}
						disabled={!props.orgMapping?.stripeOrgAccountXeroOrgSettings?.customerBalanceEnabled}
					>
						<Dropdown.Item as="div">
							<Form.Control
								type="text"
								placeholder="Search or enter value"
								value={taxFilter}
								onClick={(e: React.MouseEvent<HTMLInputElement>) => e.stopPropagation()} // Prevent dropdown from closing on input click
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTaxFilter(e.target.value)} // Handle input changes
							/>
						</Dropdown.Item>
						{props
							.mappedXeroOrg!.taxRates.filter((tr) => tr.canApplyToRevenue && tr.name!.toLowerCase().includes(taxFilter.toLowerCase()))
							.map((taxRate) => (
								<Dropdown.Item key={taxRate.xeroTaxRateID} eventKey={taxRate.xeroTaxRateID}>
									{taxRate.name} {taxRate.effectiveRate ? `(${(Number(taxRate.effectiveRate) * 100).toFixed(2)}%)` : ''}
								</Dropdown.Item>
							))}
					</DropdownButton>
				</Col>
			</Form.Group>
		</>
	)
}

export { CustomerBalanceSettings }
